import { useMemo, useState } from "react";
import { useSearchContext } from "../context/SearchContext";
import { useSearch } from "../hooks/useSearch";
import { ShowCard } from "./ShowCard";
import { DealSummary } from "./DealSummary";
import { SkeletonCard } from "./SkeletonCard";
import type { ShowWithPackages } from "../types";

type SortKey = "price" | "date" | "duration";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "price", label: "Lowest total" },
  { key: "date", label: "Soonest date" },
  { key: "duration", label: "Shortest travel" },
];

const SUGGESTIONS = [
  "Taylor Swift",
  "Kendrick Lamar",
  "Olivia Rodrigo",
  "Bad Bunny",
];

function sortShows(shows: ShowWithPackages[], key: SortKey): ShowWithPackages[] {
  const copy = [...shows];
  if (key === "price") {
    copy.sort(
      (a, b) =>
        a.packages.cheapest.total_cents - b.packages.cheapest.total_cents,
    );
  } else if (key === "date") {
    copy.sort((a, b) => a.show_date.localeCompare(b.show_date));
  } else {
    const dur = (s: ShowWithPackages): number =>
      s.packages.cheapest.flight_option?.total_duration_minutes ?? Infinity;
    copy.sort((a, b) => dur(a) - dur(b));
  }
  return copy;
}

export function ResultsList() {
  const { state, dispatch } = useSearchContext();
  const { search } = useSearch();
  const [sortKey, setSortKey] = useState<SortKey>("price");

  const results = state.results;

  const cheapestOverallCents = useMemo(() => {
    if (!results || results.shows.length === 0) return 0;
    return Math.min(
      ...results.shows.map((s) => s.packages.cheapest.total_cents),
    );
  }, [results]);

  const sorted = useMemo(
    () => (results ? sortShows(results.shows, sortKey) : []),
    [results, sortKey],
  );

  // ---- Loading: skeletons keep the layout stable
  if (state.loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-28 animate-pulse rounded-2xl bg-slate-200" />
        {[0, 1, 2, 3].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  // ---- Error
  if (state.error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <p className="font-medium text-red-800">{state.error.error}</p>
        <p className="mt-1 text-sm text-red-600">
          Please try again in a moment.
        </p>
      </div>
    );
  }

  // ---- Nothing searched yet
  if (!results) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-500">
        Enter an artist and your home airport to compare every tour stop.
      </div>
    );
  }

  // ---- Artist not found
  if (results.artist.id === "") {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
        <p className="text-lg font-semibold text-slate-900">
          We couldn&apos;t find “{results.artist.name}”
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Check the spelling, or try one of these:
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {SUGGESTIONS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => {
                dispatch({ type: "SET_ARTIST", artist: name });
                void search({ artist: name });
              }}
              className="rounded-full border border-slate-200 px-3 py-1 text-sm font-medium text-slate-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---- Found artist, but no shows survived filters
  if (results.shows.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
        <p className="text-lg font-semibold text-slate-900">
          No trips matched your filters
        </p>
        <p className="mt-1 text-sm text-slate-500">
          {results.artist.name} has no upcoming shows within your budget, travel
          time, or stop preferences. Try loosening the filters above.
        </p>
      </div>
    );
  }

  // ---- Results
  return (
    <div className="flex flex-col gap-4">
      <DealSummary results={results} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          {results.total_results}{" "}
          {results.total_results === 1 ? "city" : "cities"} for{" "}
          {results.artist.name}
        </p>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Sort by</span>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {sorted.map((show) => (
        <ShowCard
          key={show.show_id}
          show={show}
          isBestDeal={
            show.packages.cheapest.total_cents === cheapestOverallCents
          }
          cheapestOverallCents={cheapestOverallCents}
        />
      ))}
    </div>
  );
}
